<template>
  <module
    ref="module"
    id="mats"
    titleIcon="fa fa-desktop"
    :title="$t('mats')"
    :use-default-list="false"
  >
    <template slot="toolbar-global">
      <div class="row toolbar justify-content-end" v-if="inprogress == false">
        <p-button type="success" @click="showCreateModal=true">
          <i class="fa fa-plus-square"></i>
        </p-button>
        <p-button type="info" class="ml-2" @click="refresh">
          <i class="fa fa-refresh"></i>
        </p-button>
        <fg-input
          class="input-xs col-5"
          :placeholder="searchMATtxt"
          v-model="searchmat"
          addon-right-icon="nc-icon nc-zoom-split"
          v-on:keyup="search"
        >
        </fg-input>
        <!-- Sorting Controls -->
        <el-select v-model="sortField" placeholder="Sort by" class="col-3" @change="search">
          <el-option label="RAM %" value="ram_percent" />
          <el-option label="Name" value="name" />
          <el-option label="CPU %" value="cpu_percent" />
          <el-option label="Total Platforms" value="total_platforms" />
          <el-option label="Masters" value="masters" />
          <el-option label="Followers" value="followers" />
          <el-option label="MT4 Count" value="mt4_count" />
          <el-option label="MT5 Count" value="mt5_count" />
        </el-select>
        <el-select v-model="sortOrder" placeholder="Order" class="col-2" @change="search">
          <el-option label="Desc" value="desc" />
          <el-option label="Asc" value="asc" />
        </el-select>
      </div>
    </template>

    <div slot="global">
      <div v-if="!inprogress" class="alert alert-primary">
        <div class="text-right">
          <p-switch
            v-model="switches.AutoCreate"
            on-text="ON"
            off-text="OFF"
            @input="trigTog('mat_create')"
          ></p-switch>

          <span class="mx-2 text-dark">{{ $t("autocreatematbooltxt") }}</span>

          <p-switch
            v-model="switches.AutoDelete"
            on-text="ON"
            off-text="OFF"
            @input="trigTog('mat_delete')"
          ></p-switch>

          <span class="ml-2 text-dark">{{ $t("autodeletematbooltxt") }}</span>
        </div>
      </div>

      <!-- In progress -->
      <div v-if="inprogress" class="text-center">
        <h2>{{ inprogresstxt }}</h2>
        <i class="fa fa-cog fa-spin fa-huge fa-fw" style="color: #51bcda"></i>
      </div>

      <div v-else class="row">
        <!-- Modal -->
        <modal :show.sync="showCreateModal" :required="true" headerClasses="justify-content-center">
          <h5 slot="header" class="modal-title">{{ txt.modalName }}</h5>
          <div class="row" style="height: 420px; align-content: center">
            <fg-input class="col-md-12" v-model="snapshot" v-validate="modelValidations.snapshot" name="snapshot" :label="$t('snapshottxt')" :error="getError('snapshot')"></fg-input>
            <fg-input class="col-md-12" v-model="plan" v-validate="modelValidations.plan" name="plan" :label="$t('plantxt')" :error="getError('plan')"></fg-input>
            <fg-input class="col-md-12" v-model="initials" v-validate="modelValidations.initials" name="initial" :label="$t('initials')" :error="getError('initial')"></fg-input>
            <fg-input class="col-md-12" v-model="capacity" v-validate="modelValidations.capacity" type="number" min="0" name="capacity" :label="$t('capacitytxt')" :error="getError('capacity')"></fg-input>
            <form-group-autocomplete class="col-md-12"  :source="options" v-model="name" v-validate="modelValidations.matgroup" name="matgroup" @selected="selOptions" @clear="clear" :label="$t('matgrouptxt')" :error="getError('matgroup')"></form-group-autocomplete>
          </div>
          <template slot="footer">
            <div class="left-side">
              <p-button link v-on:click="createItem"> {{ txt.create }}</p-button>
            </div>
            <div class="divider"></div>
            <div class="right-side">
              <p-button type="danger" link v-on:click="showCreateModal=false"> {{ txt.cancelbtn }}</p-button>
            </div>
          </template>
        </modal>
      </div>

      <!-- No Accounts -->
      <div
        v-if="clonedMATs.length == 0 && inprogress == false"
        class="text-center"
      >
        <h2>{{ $t("nomats") }}</h2>
      </div>

      <el-row
        v-if="!inprogress && mats.length > 0"
        :gutter="14"
        style="max-height: calc(100vh - 250px); overflow-y: auto; margin: 0;"
      >
        <el-col
          v-for="mat in mats"
          :key="mat.name"
          :xs="24"
          :sm="12"
          :md="8"
          :lg="6"
          style="padding-bottom: 12px;"
        >
          <el-card class="mat-card" shadow="always">
            <!-- Robot Icon Indicator -->
            <el-tooltip :content="'Robot last reported about ' + minutesAgo(mat.last_update) + 'min ago'" placement="top">
              <i class="fa fa-android robot-icon" :class="getStatusClass(mat.last_update)" />
            </el-tooltip>

            <!-- Open MAT Button -->
            <el-tooltip content="Show MAT details..." placement="top">
              <p-button type="primary details" title="Show details..." round @click="openMAT(`${mat.id}`)">
                <i class="fa fa-ellipsis-h"></i>
              </p-button>
            </el-tooltip>

            <!-- Title -->
            <div class="card-header">
              <h6 class="mat-title">{{ mat.name }}</h6>
            </div>

            <!-- Gauges -->
            <div class="inline-blocks">
                <GaugeChart :value="mat.cpu_percent" label="CPU" />
                <GaugeChart :value="mat.ram_percent" label="RAM" />
            </div>

            <!-- Counters -->
            <div class="inline-blocks counter-wrapper">
              <div class="counter-item">
                <div class="count master-count">{{ mat.masters || 0}}</div>
                <label class="count-label">M</label>
              </div>
              <div class="counter-item">
                <div class="count follower-count">{{ mat.followers || 0}}</div>
                <label class="count-label">F</label>
              </div>
              <div class="counter-item">
                <div class="count total-count">{{ mat.total_platforms || 0}}</div>
                <label class="count-label">T</label>
              </div>
              <div class="counter-item">
                <div class="count mt4-count">{{ mat.mt4_count }}</div>
                <label class="count-label">MT4</label>
              </div>
              <div class="counter-item">
                <div class="count mt5-count">{{ mat.mt5_count }}</div>
                <label class="count-label">MT5</label>
              </div>
            </div>

          </el-card>
        </el-col>
      </el-row>

    </div>
  </module>
</template>
<script>
import Vue from "vue";
import { Card, Col, Tooltip, Row } from "element-ui";
import PSwitch from "src/components/UIComponents/PSwitch.vue";
import swal from "sweetalert2";
import {Modal} from 'src/components/UIComponents'
import Autocomplete from 'vuejs-auto-complete'
import FormGroupAutocomplete from '../../../../UIComponents/Inputs/formGroupAutocomplete.vue';
import FormGroupNumeric from '../../../../UIComponents/Inputs/formGroupNumeric.vue';
Vue.use(Card);
Vue.use(Col);
Vue.use(Row);
Vue.use(Tooltip);

const user_data = JSON.parse(localStorage.getItem("user-info"));

export default {
  components: {
    Autocomplete,
    Card,
    Col,
    FormGroupAutocomplete,
    FormGroupNumeric,
    Modal,
    PSwitch,
    Tooltip,
    Row,
  },
  data() {
    return {
      inprogress: true,
      mats: [],
      clonedMATs: [],
      searchmat: null,
      searchMATtxt: this.$t("searchmats"),
      inprogresstxt:
        this.$t("loadingtxt") +
        " " +
        this.$t("mats") +
        ". " +
        this.$t("pleasewait"),
      switches: {
        AutoCreate: true,
        AutoDelete: true,
      },
      showCreateModal: false,
      txt: {
        modalName: this.$t('matcreationsettings'),
        create: this.$t('create'),
        cancelbtn: this.$t('cancel'),
      },
      snapshot: '',
      plan: '',
      initials: '',
      capacity: '',
      matgroup: '',
      options: [],
      name: '',
      sortField: 'name',
      sortOrder: 'asc',
      modelValidations: {
        snapshot: {required: true, max: 50},
        plan: {required: true, max: 10},
        initials: {required: true, max: 5},
        capacity: {required: true},
        matgroup: {required: true, max: 30},
      },
    };
  },
  methods: {
    clear () {
      this.name = ''
    },
    refresh () {
      this.inprogress = true;
      this.clear()
      this.$getMATs()
          .then(
            (resp) => {
              this.mats.push(...resp.data || []);
              this.clonedMATs.push(...resp.data || []);
              this.inprogress = false;
            },
            this.failOp
          );
    },
    selOptions (opt) {
      this.name = opt.selectedObject.name;
    },
    getError (fieldName) {
      return this.errors.first(fieldName);
    },
    loadMATsAuto(resp) {
      this.switches.AutoCreate = !!resp.data["mat_create"];
      this.switches.AutoDelete = !!resp.data["mat_delete"];
    },
    loadGroups(resp) {
      this.options.push(...resp.data.map(opt => ({
        id: opt.id,
        name: opt.name
      })));
    },
    openMAT(matId) {
      location.href = "/admin_strategies/mat/" + matId;
    },
    failOp(error) {
      console.log(error);
    },
    search() {
      if (!this.inprogress) {
        if (this.searchmat && this.searchmat.length > 0) {
          const search = this.searchmat.toLowerCase();
          this.mats = this.clonedMATs.filter(mat => {
            const nameMatch = mat.name.toLowerCase().includes(search);
            const platformMatch = mat.platforms.some(p => String(p.account).toLowerCase().includes(search));
            return nameMatch || platformMatch;
          });
        } else {
          this.mats = [...this.clonedMATs];
        }
        this.sort();
      }
    },
    sort() {
      this.mats = this.mats.sort((a, b) => {
        const aVal = a[this.sortField];
        const bVal = b[this.sortField];
        const direction = this.sortOrder === 'asc' ? 1 : -1;
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          return aVal.localeCompare(bVal) * direction;
        }
        return (aVal - bVal) * direction;
      });
    },
    trigTog(flag_name) {
      if (flag_name == "mat_create") {
        var flag_val = this.switches.AutoCreate;
      } else {
        var flag_val = this.switches.AutoDelete;
      }
      this.$setMATsAuto({ flag_name: flag_name, flag_value: +flag_val }).then(
        this.matScriptChanged,
        this.failOp
      );
    },
    matScriptChanged() {
      console.log("successfully changed mat script");
    },
    operationConcluded() {
      this.snapshot = ''
      this.plan = ''
      this.initials = ''
      this.capacity = ''
      this.matgroup = ''
      this.inProgress = false
    },
    createItem(){
      let param = {
        snapshot: this.snapshot,
        plan: this.plan,
        initials: this.initials,
        capacity: this.capacity,
        mat_group: this.name
      }
      this.UpdateMatSettings(param).then(this.createItemSuccess, this.failOp)
    },
    createItemSuccess(resp){
      if(!resp.success) {
        return this.failOp()
      }
      this.showCreateModal = false
      swal('Good job!', 'New item created', 'success').then( () => {
        this.operationConcluded();
      })
    },
    minutesAgo(timestamp) {
      const now = Date.now();
      const updated = new Date(timestamp).getTime();
      return Math.floor((now - updated) / 60000); // in minutes
    },
    getStatusClass(timestamp) {
      return this.minutesAgo(timestamp) > 10 ? 'red' : 'green';
    },
  },
  mounted() {
    this.$getMATsAuto().then(this.loadMATsAuto, this.failOp);
    this.$getMatGroups().then(this.loadGroups, this.failOp);
    this.refresh();
  },
  created() {
    this.$setTranslatingInProgress();
  },
  beforeDestroy() {
    this.$root.$off("showTranslatingProgress");
  },
};
</script>
<style scoped lang="scss">
.row.toolbar {
  padding: 0 0.75rem;
  > [class^="col-"],
  > [class*=" col-"] {
    padding-right: 0 !important;
  }
}
.count {
  border-radius: 50px;
  -moz-border-radius: 50px;
  -webkit-border-radius: 50px;
  display: inline-block;
  font-weight: bold;
  height: 32px;
  line-height: 32px;
  text-align: center;
  min-width: 2.5rem;
  padding: 0;
  font-size: 1rem;
}
.master-count {
  color: #ee9f3d;
  border: 3px solid #ee9f3d;
}
.follower-count {
  color: #35c399;
  border: 3px solid #35c399;
}
.total-count {
  height: 48px;
  padding: 5px 12px;
  font-size: 2rem;
  color: #3f79e8;
  border: 3px solid #3f79e8;
}
.mt4-count {
  color: #bd77e6;
  border: 3px solid #bd77e6;
}
.mt5-count {
  color: #7466ef;
  border: 3px solid #7466ef;
}
.mat-card::v-deep {
  min-width: 280px;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  .el-card__body {
    padding-bottom: 0 !important;
  }
}
.details {
  position: absolute;
  top: 6px;
  left: 10px;
  font-size: 1.5rem;
  line-height: 1.5rem;
  padding: 0.25rem 0.5rem;
}
/* Robot in top-right */
.robot-icon {
  position: absolute;
  top: 10px;
  right: 10px;
  font-size: 2.5rem;
}
.robot-icon.green {
  color: #67C23A;
}
.robot-icon.yellow {
  color: #E6A23C;
}
.robot-icon.red {
  color: #F56C6C;
}
/* Title */
.card-header {
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
  padding: 0 !important;
}
.card-footer {
  text-align: center;
  padding: 0;
}
.mat-title {
  font-size: 16px;
  font-weight: bold;
  color: #409EFF;
  text-align: center;
  margin: 0;
}
/* Gauges & Counters */
.inline-blocks {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  margin-bottom: 12px;
}
.inline-blocks.counter-wrapper {
  gap: 0.15rem;
}
.counter-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0 4px;
}
.count-label {
  font-weight: bold;
  text-align: center;
}
</style>
